<template>
  <div v-if="isLoading" class="loader-overlay" :class="{ 'is-hidden': !show }">

    <!-- Rising particles -->
    <div class="particles">
      <span v-for="n in 20" :key="'p'+n" class="particle" :style="particleStyle(n)"></span>
    </div>

    <!-- Subtle radial light -->
    <div class="ambient-light"></div>

    <!-- Center content -->
    <div class="center-content">

      <!-- Elegant spinning arc -->
      <div class="arc-wrapper">
        <svg class="arc-svg" viewBox="0 0 200 200">
          <defs>
            <linearGradient id="arcGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#00E5FF" stop-opacity="1"/>
              <stop offset="50%" stop-color="#c6f605" stop-opacity="0.8"/>
              <stop offset="100%" stop-color="#00E5FF" stop-opacity="0"/>
            </linearGradient>
          </defs>
          <circle class="arc-track" cx="100" cy="100" r="92" />
          <circle class="arc-fill" cx="100" cy="100" r="92" />
        </svg>
      </div>

      <!-- Logo -->
      <div class="logo-wrapper">
        <img :src="mahercoLogo" alt="Maherco" class="logo" />
      </div>
    </div>

    <!-- Brand + progress -->
    <div class="bottom-section">
      <p class="brand-name">MAHERCO</p>
      <div class="progress-track">
        <div class="progress-fill" :style="{ width: progress + '%' }"></div>
      </div>
      <p class="tagline">{{ loadingText }}</p>
    </div>
  </div>
</template>

<style scoped>
.loader-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #0B0D14;
  transition: opacity 0.7s ease;
  overflow: hidden;
  font-family: 'Oxanium', sans-serif;
}
.loader-overlay.is-hidden {
  opacity: 0;
  pointer-events: none;
}

/* Ambient light behind logo */
.ambient-light {
  position: absolute;
  width: 340px;
  height: 340px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(0,229,255,0.08) 0%, transparent 70%);
  pointer-events: none;
  animation: breathe 3s ease-in-out infinite;
}
@keyframes breathe {
  0%, 100% { transform: scale(1); opacity: 0.5; }
  50%      { transform: scale(1.2); opacity: 1; }
}

/* Rising particles */
.particles {
  position: absolute;
  inset: 0;
  overflow: hidden;
  pointer-events: none;
}
.particle {
  position: absolute;
  bottom: -10px;
  border-radius: 50%;
  animation: rise linear infinite;
}
@keyframes rise {
  0%   { transform: translateY(0) scale(1); opacity: 0; }
  10%  { opacity: 1; }
  80%  { opacity: 0.6; }
  100% { transform: translateY(-110vh) scale(0.3); opacity: 0; }
}

/* Center group */
.center-content {
  position: relative;
  width: 180px;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* SVG Arc spinner */
.arc-wrapper {
  position: absolute;
  inset: 0;
  animation: spinArc 2.5s linear infinite;
}
@keyframes spinArc {
  to { transform: rotate(360deg); }
}
.arc-svg {
  width: 100%;
  height: 100%;
}
.arc-track {
  fill: none;
  stroke: rgba(255,255,255,0.03);
  stroke-width: 1.5;
}
.arc-fill {
  fill: none;
  stroke: url(#arcGrad);
  stroke-width: 2;
  stroke-linecap: round;
  stroke-dasharray: 160 420;
}

/* Logo */
.logo-wrapper {
  position: relative;
  z-index: 2;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background: #12151d;
  border: 1.5px solid rgba(0,229,255,0.15);
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 0 40px rgba(0,229,255,0.12), inset 0 0 20px rgba(0,229,255,0.05);
}
.logo {
  width: 70%;
  height: 70%;
  object-fit: contain;
  filter: drop-shadow(0 0 4px rgba(255,255,255,0.1));
}

/* Bottom section */
.bottom-section {
  margin-top: 56px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  width: 200px;
}
.brand-name {
  font-size: 20px;
  font-weight: 800;
  letter-spacing: 0.35em;
  color: #fff;
  margin: 0;
}

/* Progress bar */
.progress-track {
  width: 100%;
  height: 3px;
  background: rgba(255,255,255,0.04);
  border-radius: 4px;
  overflow: hidden;
}
.progress-fill {
  height: 100%;
  border-radius: 4px;
  background: linear-gradient(90deg, #00E5FF, #c6f605);
  box-shadow: 0 0 8px rgba(0,229,255,0.5);
  transition: width 80ms linear;
}

/* Tagline */
.tagline {
  font-size: 10px;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.25);
  margin: 0;
  transition: opacity 0.4s ease;
}
</style>

<script>
import mahercoLogo from '@/assets/revamp/MHC-LOGO 2.png';

export default {
  name: "PageLoader",
  data() {
    return {
      isLoading: true,
      show: true,
      mahercoLogo,
      progress: 0,
      loadingText: 'Preparing your experience',
      loadingTexts: [
        'Preparing your experience',
        'Shuffling the deck',
        'Spinning the wheel',
        'Loading rewards'
      ],
      progressInterval: null,
      textInterval: null
    };
  },
  methods: {
    startLoader() {
      this.progress = 0;
      clearInterval(this.progressInterval);
      clearInterval(this.textInterval);

      // Smooth progress to 100% in ~3s
      this.progressInterval = setInterval(() => {
        if (this.progress < 100) {
          this.progress += 1;
        } else {
          clearInterval(this.progressInterval);
        }
      }, 30);

      // Cycle taglines
      let idx = 0;
      this.textInterval = setInterval(() => {
        idx = (idx + 1) % this.loadingTexts.length;
        this.loadingText = this.loadingTexts[idx];
      }, 1200);
    },
    stopLoader() {
      clearInterval(this.progressInterval);
      clearInterval(this.textInterval);
    },
    particleStyle(n) {
      const colors = ['rgba(0,229,255,0.4)', 'rgba(198,246,5,0.3)', 'rgba(255,255,255,0.15)'];
      const left = ((n * 47 + 11) % 100);
      const size = 2 + (n % 4) * 1.5;
      const dur = 5 + (n % 6) * 1.5;
      const delay = ((n * 0.6) % 5);
      return {
        left: left + '%',
        width: size + 'px',
        height: size + 'px',
        background: colors[n % 3],
        boxShadow: `0 0 ${size + 2}px ${colors[n % 3]}`,
        animationDuration: dur + 's',
        animationDelay: delay + 's'
      };
    }
  },
  mounted() {
    // Only show loader once per session (clears when browser/tab is closed)
    if (sessionStorage.getItem('maherco-loader-shown')) {
      this.isLoading = false;
      this.show = false;
      return;
    }

    this.startLoader();

    // Hide after 3 seconds, then mark session as loaded
    setTimeout(() => {
      this.show = false;
      setTimeout(() => {
        this.isLoading = false;
        this.stopLoader();
        sessionStorage.setItem('maherco-loader-shown', 'true');
      }, 700);
    }, 3000);
  },
  unmounted() {
    this.stopLoader();
  }
};
</script>
